import React from "react"

import { useMetricRecordMetadataById } from "@/metrics/transport-data"
import { Card, CardContent } from "@coval/ui/card"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import { formatNumber } from "@/utils/formatting/format-number"

import {
  DerivedAggregateConfig,
  type AggregateMetadata,
  type AggregationType,
} from "../../components/derived/DerivedAggregateConfig"
import { NumericalMetricRecord } from "../base/NumericalMetricRecord"

interface DerivedAggregateRawValues {
  source_values: Record<string, number | null>
  skipped_null_count: number
  aggregation_type: AggregationType
}

interface DerivedAggregateResult {
  unit?: string | null
  count: number
  raw_values: DerivedAggregateRawValues
}

function parseDerivedAggregateResult(
  mo: RunMetricOutput,
): DerivedAggregateResult | null {
  const result = mo.result as Record<string, unknown> | undefined
  if (!result) {
    return null
  }
  if (typeof result.raw_values !== "object" || result.raw_values === null) {
    return null
  }
  return result as unknown as DerivedAggregateResult
}

function formatAggregatedValue(
  value: number | null | undefined,
  unit?: string | null,
): string {
  if (value == null) {
    return "—"
  }
  if (unit === "severity" || unit === "ratio") {
    return value.toFixed(2)
  }
  if (unit === "s") {
    return `${value.toFixed(2)} s`
  }
  if (unit === "percent") {
    // Backend stores percent values as 0-100 already.
    return `${value.toFixed(1)}%`
  }
  return formatNumber(value).toString()
}

const ParentValuesTable: React.FC<{
  sourceValues: Record<string, number | null>
  unit?: string | null
}> = ({ sourceValues, unit }) => {
  const { metricRecordsById } = useMetricRecordMetadataById()

  return (
    <table className="w-full text-sm" aria-label="Parent metric values">
      <thead>
        <tr className="text-left text-coval-text-muted border-b border-coval-border">
          <th className="pb-2 pr-4 font-medium">Parent metric</th>
          <th className="pb-2 font-medium">Value</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(sourceValues).map(([parentId, value]) => {
          const name =
            metricRecordsById[parentId]?.metric_display_name ?? parentId
          return (
            <tr
              key={parentId}
              className="border-b border-coval-border last:border-0"
            >
              <td className="py-2 pr-4 text-coval-text truncate max-w-xs">
                {name}
              </td>
              <td className="py-2">
                {value == null ? (
                  <span className="text-coval-text-muted italic">SKIPPED</span>
                ) : (
                  formatAggregatedValue(value, unit)
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export class DerivedAggregateMetricRecord extends NumericalMetricRecord {
  _renderDeepDive(mo: RunMetricOutput, ..._rest: unknown[]): React.ReactNode {
    const parsed = parseDerivedAggregateResult(mo)
    const displayValue = formatAggregatedValue(mo.float_value, parsed?.unit)
    const aggregationType = parsed?.raw_values?.aggregation_type

    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col items-center gap-1">
              {aggregationType && (
                <span className="text-xs font-medium text-coval-text-muted uppercase tracking-wide">
                  {aggregationType}
                </span>
              )}
              <span
                className="text-3xl font-bold text-coval-text"
                aria-label={`Aggregated value: ${displayValue}`}
              >
                {displayValue}
              </span>
              {parsed?.unit && (
                <span className="text-xs text-coval-text-muted">
                  {parsed.unit}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {parsed?.raw_values?.source_values && (
          <Card>
            <CardContent className="pt-4">
              <ParentValuesTable
                sourceValues={parsed.raw_values.source_values}
                unit={parsed.unit}
              />
              {parsed.raw_values.skipped_null_count > 0 && (
                <p className="mt-3 text-xs text-coval-text-muted">
                  {parsed.raw_values.skipped_null_count} parent
                  {parsed.raw_values.skipped_null_count > 1 ? "s" : ""} skipped
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    )
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
  ): React.ReactNode {
    if (!metricRecordData.metric_metadata) {
      return null
    }
    const metadata = metricRecordData.metric_metadata as AggregateMetadata
    const handleChange = (next: AggregateMetadata) => {
      onChange({ ...metricRecordData, metric_metadata: next })
    }
    return (
      <DerivedAggregateConfig
        metadata={metadata}
        onChange={handleChange}
        currentMetricId={metricRecordData.id ?? null}
      />
    )
  }

  renderMetricType(): React.ReactNode {
    return "Derived Aggregate"
  }
}
